import React, { FC } from "react"
import { useSelector } from "react-redux"
import { BodyWithLeftSidebarLayout } from "../components/views/atoms/Layouts"
import { CenteredLoadingIndicator, PageHeader } from "../components/views/molecules/Molecules"
import { MasterLayout } from "../components/views/atoms/layouts/MasterLayout"
import { Redirect, Route, Switch } from "react-router-dom"
import { Page } from "../components/dynamic-content/Page"
import { Paths } from "../utils/Paths"
import { IPage, IPageSet } from "contentful-types"
import { useContentfulConditionalGate } from "../lib/contentful/components/ContentfulConditionGate"
import { MainLayout } from "components/views/app/domainIsolation/MainLayout"
import { ErrorPage } from "components/views/app/ErrorPage"
import { FlexContainer } from "@carrotfertility/carotene"
import { DigitalBenefitsGuideNav } from "../components/digital-benefit-guide/DBGNav"
import { useQuery, UseQueryResult } from "@tanstack/react-query"
import ResponseError from "types/responseError"
import { getPreferredLocale } from "reducers/userInfo"
import buildCarrotApiRetryPolicy from "utils/CarrotApiRetryPolicy"
import { getContentfulClient } from "lib/contentful/contentfulClient"
import useBenefit from "components/dynamic-content/hooks/useGetBenefit"
import { createErrorBoundary } from "../utils/createErrorBoundary"
import { ProgramType } from "components/dynamic-content/Benefit"
import { Container } from "@carrotfertility/carotene-core"
import { useFlags } from "launchdarkly-react-client-sdk"
/* eslint-disable import/no-restricted-paths */
import { ContentfulContentRenderer } from "services/contentful"
import { defaultMarkdownElements } from "services/markdown"
/* eslint-enable import/no-restricted-paths */

export const GET_PAGE_SETS_QUERY_KEY = "GET_PAGE_SETS"

const getDbgPageSets = async (locale: string): Promise<IPageSet[]> => {
  const client = getContentfulClient()
  const { items } = await client.getEntries({
    content_type: "pageSet",
    include: 10,
    locale
  })
  return items as IPageSet[]
}

const useQueryDbgPageSets = (locale: string): UseQueryResult<IPageSet[], ResponseError> => {
  return useQuery<IPageSet[], ResponseError>([GET_PAGE_SETS_QUERY_KEY, locale], () => getDbgPageSets(locale), {
    refetchOnWindowFocus: false,
    retry: buildCarrotApiRetryPolicy(3)
  })
}

const YourBenefitGuide: FC = () => {
  const preferredLocale = useSelector(getPreferredLocale)
  const { data: pageSets, isLoading, isError } = useQueryDbgPageSets(preferredLocale)
  const { data: benefit, isLoading: loadingBenefit } = useBenefit()

  if (isLoading || loadingBenefit) {
    return <CenteredLoadingIndicator style={{ height: "100%" }} />
  }
  if (isError) {
    return (
      <MainLayout>
        <FlexContainer justifyContent="center" alignItems="center" height="calc(100% - 133px)">
          <ErrorPage />
        </FlexContainer>
      </MainLayout>
    )
  }
  const BenefitGuideErrorBoundary = createErrorBoundary(ErrorPage)

  // maintaining the naming convention in here to match Contentful PageSet's name 'digital_benefits_guide'
  const digitalBenefitsGuideFields = pageSets.find((pageSet) =>
    benefit.program.type === ProgramType.PRO
      ? pageSet?.fields.key === "pro_digital_benefits_guide"
      : pageSet?.fields.key === "core_digital_benefits_guide"
  ).fields
  const topLevelPages = digitalBenefitsGuideFields?.topLevelPages ?? []

  return (
    <MasterLayout
      header={
        <PageHeader icon={digitalBenefitsGuideFields?.icon.fields.file.url} title={digitalBenefitsGuideFields?.title} />
      }
    >
      <BenefitGuideErrorBoundary>
        <BodyWithLeftSidebarLayout sideBarElements={[<DigitalBenefitsGuideNav pages={topLevelPages} key="DBG_nav" />]}>
          <div className="w-100">
            <Switch>
              <Redirect
                exact
                from={Paths.YOUR_BENEFIT_GUIDE}
                to={`${Paths.YOUR_BENEFIT_GUIDE}/${topLevelPages[0]?.fields.slug}`}
              />
              {RoutesToPages({ pages: topLevelPages, baseUrl: Paths.YOUR_BENEFIT_GUIDE })}
              <Redirect to={`${Paths.YOUR_BENEFIT_GUIDE}/${topLevelPages[0]?.fields.slug}`} />
            </Switch>
          </div>
        </BodyWithLeftSidebarLayout>
      </BenefitGuideErrorBoundary>
    </MasterLayout>
  )
}

type RoutesToPagesFields = {
  pages: IPage[]
  baseUrl: string
}

function BenefitGuidePage(props: { page: IPage }) {
  return (
    <Container maxWidth="md" disableGutters component="article">
      <defaultMarkdownElements.h1.component {...defaultMarkdownElements.h1.props} component="h2">
        {props.page.fields?.title}
      </defaultMarkdownElements.h1.component>
      <ContentfulContentRenderer contents={props.page.fields?.content} />
    </Container>
  )
}

const PageOrRedirect = ({ pages }: { pages: IPage[] }): JSX.Element => {
  const { newAccessibleMarkdown } = useFlags()

  const pageConditions = pages?.map((page, i) => {
    return {
      element: newAccessibleMarkdown ? <BenefitGuidePage page={page} key={i} /> : <Page page={page} key={i} />,
      conditions: page.fields.conditions
    }
  })
  const pageContents = useContentfulConditionalGate(pageConditions ? pageConditions : [])
  return pageContents.length === 0 ? (
    <Redirect to={`${Paths.YOUR_BENEFIT_GUIDE}/${pages[0]?.fields.slug}`} />
  ) : (
    <>{pageContents}</>
  )
}

const findPagesBySlug = (pages: Array<IPage>, slug: string): IPage[] =>
  pages
    .flat(Infinity)
    .filter((a) => a !== undefined)
    .filter((a) => a.fields.slug === slug)

const RoutesToPages = ({ pages, baseUrl }): RoutesToPagesFields[] => {
  if (!pages) return []
  return pages
    ?.map(
      (page) =>
        page.fields && [
          <Route
            key={page.sys.id}
            exact
            path={`${baseUrl}/${page.fields.slug}`}
            render={() => <PageOrRedirect pages={findPagesBySlug(pages, page.fields.slug)} />}
          />,
          ...RoutesToPages({ pages: page.fields.childPages, baseUrl: `${baseUrl}/${page.fields.slug}` })
        ]
    )
    .flat()
}

export { YourBenefitGuide }
